import { Fragment } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Alert, Badge } from 'components/common';
import { Icon } from 'components/common/icons';
import { TaskPointsBadge } from '.';

import type { H5PUserAnswers } from 'components/reader/blocks';

interface Props {
  answers: H5PUserAnswers;
  completion: number;
  hideScore?: boolean;
  maxScore: number;
  missingCorrectAnswers?: boolean;
  points: number;
  score: number;
  showPreview: boolean;
  success: number;
  task: any;
}

const H5PAnswer = ({
  answers,
  completion,
  hideScore = false,
  maxScore,
  missingCorrectAnswers,
  points,
  showPreview,
  success,
  score,
  task
}: Props) => {
  const { messages, theme } = useReduxState();

  const getStatusSummary = (
    label: string,
    summary?: React.ReactNode | number,
    isScore = false,
    isRigthAnswer?: boolean
  ) => {
    return (
      <div className="status">
        <div className="status-content">
          <div className="status-text">{label}</div>

          <div className="status-info">
            {isScore ? (
              <div className="status-percentage">
                {typeof summary !== 'number' ? (
                  <div className="status-percentage-content">
                    {messages.commonUnknown}
                  </div>
                ) : (
                  <div className="status-percentage-content">
                    {`${Math.round(summary)}%`}

                    <div className="status-percentage-indicator">
                      <div
                        className="status-percentage-indicator-fill"
                        style={{ width: `${Math.round(summary)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {summary ? (
                  <div className="status-summary">{summary}</div>
                ) : null}
                {isRigthAnswer !== undefined ? (
                  isRigthAnswer ? (
                    <div
                      aria-label={messages.commonCorrectAnswer}
                      data-tooltip={messages.commonCorrectAnswer}
                      className="status-icon"
                    >
                      <Icon color={theme.successStandaloneColor} icon="check" />
                    </div>
                  ) : (
                    <div
                      aria-label={messages.commonIncorrectAnswer}
                      data-tooltip={messages.commonIncorrectAnswer}
                      className="status-icon"
                    >
                      <Icon color={theme.dangerColor} icon="times" />
                    </div>
                  )
                ) : null}
              </div>
            )}
          </div>
        </div>

        <style jsx>{`
          .status {
            background-color: white;
            border-radius: 5px;
            color: ${theme.textColorLight};
            font-size: 1.4rem;
            padding: 8px 10px;
          }
          .status:not(:first-child) {
            margin-top: 10px;
          }
          .status-text {
            display: flex;
            flex-grow: 1;
            flex-shrink: 1;
          }
          .status-text-inner {
            word-break: break-word;
          }
          .status-text-inner :global(p) {
            margin: 8px 0 0;
          }
          .status-text-inner :global(p:first-child) {
            margin-top: 0;
          }
          .status-info {
            display: flex;
          }
          .status-correct {
            flex-grow: 1;
            flex-shrink: 0;
            font-size: 1.2rem;
            margin-left: 10px;
            min-width: 0;
            text-align: right;
          }
          .status-percentage-content {
            align-items: center;
            background-color: ${theme.lightGrey};
            border-radius: 4px;
            display: flex;
            font-size: 1.2rem;
            font-weight: 600;
            padding: 1px 5px;
            white-space: nowrap;
            width: 85px;
            justify-content: center;
          }
          .status-percentage-indicator {
            background-color: ${theme.lightGreyDark};
            border-radius: 3px;
            display: inline-block;
            height: 6px;
            margin-left: 5px;
            overflow: hidden;
            width: 50px;
          }
          .status-summary {
            font-size: 1.5rem;
          }
          .status-icon {
            font-size: 1.2rem;
            position: relative;
            top: 1px;
          }
          @keyframes statusPercentageFill {
            0% {
              width: 0;
            }
          }
          .status-percentage-indicator-fill {
            animation: statusPercentageFill 1s;
            background-color: ${theme.successColor};
            height: 100%;
          }
          @media (max-width: ${breakpoints.small - 1}px) {
            .status-info {
              margin-top: 10px;
            }
          }
          @media (min-width: ${breakpoints.small}px) {
            .status-content {
              display: flex;
            }
          }
        `}</style>
      </div>
    );
  };

  const handleImageError = (
    ev: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const target = ev.target as HTMLImageElement;
    target.src = '/static/img/block_placeholder.png';
    target.parentElement.classList.add('broken');
  };

  const title = task?.data?.file?.title?.replace(/.zip$/, '').replace('_', ' ');

  const blockThumbnailUrl = task?.data?.file?.baseUrl?.replace(
    /uploads\/decompressed.*$/,
    `blocks/${task.id}.png`
  );

  const completionIcon = (
    <Badge
      category="success-light"
      icon="check"
      isRounded={false}
      size="small"
      text={messages.tasksScormStatusCompletion}
    />
  );
  const successIcon = (
    <Badge
      category="success-light"
      icon="check"
      isRounded={false}
      size="small"
      text={messages.tasksScormStatusSuccess}
    />
  );
  const inProgressIcon = (
    <Badge
      category="default"
      icon="hourglass-start"
      isRounded={false}
      size="small"
      text={messages.commonInProgress}
    />
  );
  const failIcon = (
    <Badge
      category="error-light"
      icon="times"
      isRounded={false}
      size="small"
      text={messages.readerCompletionFailed}
    />
  );

  const version1 = !(task.data.version > 1);

  const userAnswers = Array.isArray(answers)
    ? answers
        .map((ans) =>
          Object.keys(ans).map((option) => ({
            answer: option,
            correct: ans[option]
          }))
        )
        .flat()
    : typeof answers === 'object'
    ? Object.keys(answers).map((ans) => ({
        answer: ans,
        correct: answers[ans]
      }))
    : [];

  return (
    <div className="answer">
      {showPreview && (
        <div className="block-preview">
          <img
            alt={title}
            title={title}
            onError={handleImageError}
            src={blockThumbnailUrl}
          />
        </div>
      )}
      <div className={'summary' + (showPreview ? ' summary-with-preview' : '')}>
        {userAnswers?.length ? (
          userAnswers.map((answer, i) => (
            <Fragment key={i}>
              {getStatusSummary(answer.answer, null, false, answer.correct)}
            </Fragment>
          ))
        ) : (
          <>
            {getStatusSummary(
              messages.tasksScormStatusCompletion,
              completion > 0 ? completionIcon : inProgressIcon
            )}
            {!isNaN(success) &&
              getStatusSummary(
                messages.tasksScormStatusSuccess,
                success >= 0.8 ? successIcon : failIcon
              )}
            {!isNaN(score) &&
              getStatusSummary(
                messages.tasksScormStatusScore,
                score * 100,
                true
              )}
          </>
        )}

        {!version1 && missingCorrectAnswers ? (
          <Alert
            size="small"
            style={{ marginTop: 15 }}
            text={messages.blockH5PMissingAnswer}
            type="warning-light"
          />
        ) : null}

        {!version1 && !success ? (
          <Alert
            size="small"
            style={{ marginTop: 10 }}
            text={messages.blockH5PSuccessRequired}
            type="warning-light"
          />
        ) : null}
      </div>

      {!hideScore ? (
        <TaskPointsBadge
          hideSuccessBadge={showPreview && hideScore}
          maxPoints={maxScore}
          points={points}
        />
      ) : null}

      <style jsx>{`
        .answer {
          color: ${theme.textColor};
          font-size: 1.5rem;
        }
        .content {
          background-color: white;
          border-radius: 5px;
          padding: 15px;
        }
        .block-preview {
          height: 124px;
          width: 160px;
          overflow: hidden;
          position: relative;
          border: 1px solid ${theme.lightGreyDark};
          border-radius: 5px;
          background: white;
          display: inline-block;
        }
        .block-preview img {
          max-width: 160px;
        }
        .summary {
          display: inline-block;
          vertical-align: top;
          width: 100%;
        }
        .summary-with-preview {
          margin-left: 10px;
          width: calc(100% - 170px);
        }
      `}</style>
    </div>
  );
};

export default H5PAnswer;
